import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
    DataList,
    DataFormFieldChangeParamType,
    NotificationType,
    PageFilterSendType,
    PageGenericParamType,
    PageContainer,
    Loader,
    PageContainerActionParamType,
    SORT_ASC,
    SORT_DESC,
    getPathObject,
    AuthMenuType,
    useStateContext,
} from "@vegi.toenlioe/novitate-react-components";
import Menu from "../../../models/Menu";
import { useNavigate } from 'react-router-dom';
import {
    Delete as DeleteIcon,
    Search as SearchIcon,
    Sort as SortIcon,
    SwapVert as SwapVertIcon,
} from '@mui/icons-material';
import { FlippedSortIcon } from "../../../styles";
import { MenuItems } from "../../../utils/Menu";

const groupName: string = "Umum";
const subgroupName: string = "Pengaturan";
const code: string = "GLOBAL-MENU";
const title: string = "Menu";
const endpoint = "menu";

function pathObject(): (PageContainerActionParamType | null) {
    return getPathObject(MenuItems, groupName, subgroupName, code);
};

function List() {
    const {authState, setAuthState, apiCaller} = useStateContext();
    const [error, setError] = useState<NotificationType | null>(null);
    const [loading, setLoading] = useState(false);
    const [filters, setFilters] = useState<PageFilterSendType[]>([]);
    const [sort, setSort] = useState<PageGenericParamType | undefined>(undefined);
    const [shouldFilterData, setShouldFilterData] = useState(false);
	const [needReload, setNeedReload] = useState(false);
    const [pageNumber, setPageNumber] = useState<number>(1);
    const [pageCount, setPageCount] = useState<number>(1);
    const filtersRef = useRef<PageFilterSendType[]>([]);
    const navigate = useNavigate();
            
    const menuAuthState: AuthMenuType | undefined = useMemo(() => {
        return authState?.daftar_menu?.filter(m => m.kode_menu === code)[0];
    }, [authState]);

    useEffect(() => {
        setNeedReload(true);
    }, []);

    useEffect(() => {
        if (!needReload)
            return;

        setNeedReload(false);
    }, [needReload]);
        
    const handleFilterChanged = useCallback((filter: DataFormFieldChangeParamType<any>) => {
        const currentFilter = filtersRef.current.filter((f) => f.name === filter.fieldName)[0];

        if (!currentFilter)
        {
            if (!(filter.data as any)[filter.fieldName])
                return;

            filtersRef.current.push({
                name: filter.fieldName,
                value: (filter.data as any)[filter.fieldName],
            })
            return;
        }

        const currentFilterIndex = filtersRef.current.indexOf(currentFilter);

        if (!(filter.data as any)[filter.fieldName])
            filtersRef.current.splice(currentFilterIndex, 1);
        
        if ((filter.data as any)[filter.fieldName])
            filtersRef.current.splice(currentFilterIndex, 1, {name: filter.fieldName, value: (filter.data as any)[filter.fieldName]});
    }, [filtersRef.current]);

    const handleSortChanged = useCallback((key: string) => {
        const newSort: PageGenericParamType = sort ? JSON.parse(JSON.stringify(sort)) : ({name: key});

        if (!newSort.value)
        {
            newSort.value = SORT_ASC;
            setSort(newSort);
            setNeedReload(true);
            return;
        }

        if (newSort.value === SORT_ASC)
        {
            newSort.value = SORT_DESC;
            setSort(newSort);
            setNeedReload(true);
            return;
        }

        if (newSort.value === SORT_DESC)
        {
            setSort(undefined);
            setNeedReload(true);
            return;
        }
    }, [sort]);

    return (
		<>
        {
            loading && <Loader />
        }
            <PageContainer
                navigate={navigate}
                menuItems={MenuItems}
                groupName={groupName}
                subgroupName={subgroupName}
                code={code}
                title={title}
                toolbars={[]}
                filter={{
                    fields: [
                        {
                            fieldName: "kode",
                            fieldOperator: "contains",
                            fieldType: "string",
                            title: "Kode Menu",
                            inputType: "text",
                            gridSizeXl: 12,
                            gridSizeLg: 12,
                            gridSizeMd: 12,
                            onChanged: handleFilterChanged,
                            canSort: true,
                            titleHasCommands: true,
                            titleCommands: [
                                {
                                    title: "Urut Kode Menu",
                                    action: () => handleSortChanged("sort-kode"),
                                    icon: sort?.name === "sort-kode" ? (sort?.value === SORT_DESC ? SortIcon : FlippedSortIcon) : SwapVertIcon,
                                    id: "sort-kode",
                                    variant: sort?.name === "sort-kode" ? "contained" : "outlined",
                                },
                            ]
                        },
                        {
                            fieldName: "nama",
                            fieldOperator: "contains",
                            fieldType: "string",
                            title: "Nama Menu",
                            inputType: "text",
                            gridSizeXl: 12,
                            gridSizeLg: 12,
                            gridSizeMd: 12,
                            onChanged: handleFilterChanged,
                            canSort: true,
                            titleHasCommands: true,
                            titleCommands: [
                                {
                                    title: "Urut Nama Menu",
                                    action: () => handleSortChanged("sort-nama"),
                                    icon: sort?.name === "sort-nama" ? (sort?.value === SORT_DESC ? SortIcon : FlippedSortIcon) : SwapVertIcon,
                                    id: "sort-nama",
                                    variant: sort?.name === "sort-nama" ? "contained" : "outlined",
                                },
                            ]
                        },
                    ],
                    saveActionIcon: SearchIcon,
                    saveActionCaption: "Cari",
                    defaultFilters: filters,
                }}
                customFormCommands={[
                    {
                        title: "Update Filter",
                        action: () => {
                            setFilters(filtersRef.current);
                            setShouldFilterData(true);
                            setNeedReload(true);
                        },
                        icon: SearchIcon,
                        color: "primary",
                    },
                    {
                        title: "Hapus Filter",
                        action: () => {
                            setFilters([]);
                            setShouldFilterData(false);
                            setNeedReload(true);
                        },
                        icon: DeleteIcon,
                    }
                ]}
                onSetPageNumber={(value: number | undefined) => setPageNumber(value ?? 1)}
                pageCount={pageCount}
            >
                <DataList<Menu>
                    dataType={Menu}
                    keyField="ucode"
                    details={[
                        {title: "Kode", fieldName: "kode"},
                        {title: "Nama", fieldName: "nama"},				
					]}
					setError={(errorState) => setError(errorState ?? null)}
                    setLoading={(loadingState) => setLoading(loadingState ?? false)}
                    actions={[]}
                    endpoint={endpoint}
                    filters={shouldFilterData ? filters : []}
                    sort={sort}
                    titleColumnName="nama"
                    needReload={needReload}
                    pageNumber={pageNumber}
                    onSetPageCount={(value: number | undefined) => setPageCount(value ?? 1)}				
                />
            </PageContainer>
		</>
    );
};

export default List;